import React from "react";
import { Download, Mail, Filter, X, Check, RefreshCw, Banknote } from "lucide-react";
import api from "../../lib/api";
import { ManualPaymentDialog } from '../../components/admin/ManualPaymentDialog';
import { ManualPaymentHistory } from '../../components/admin/ManualPaymentHistory';

const STATUS_LABELS = {
  pending: { label: "En attente", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30" },
  paid: { label: "Payée", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  confirmed: { label: "Confirmée", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  cancelled: { label: "Annulée", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  completed: { label: "Terminée", cls: "bg-text-cream/15 text-text-cream/70 border-white/15" },
  partial: { label: "Paiement partiel", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30" },
  partially_paid: { label: "Payée partiellement", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30" },
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminBookings() {
  const [bookings, setBookings] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [filters, setFilters] = React.useState({ status: "", service_slug: "", from_date: "", to_date: "" });
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [manualBooking, setManualBooking] = React.useState(null);
  const [retrying, setRetrying] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [b, s, st] = await Promise.all([
        api.get(`/admin/bookings?${params}`),
        api.get(`/services`),
        api.get(`/admin/bookings/stats`),
      ]);
      setBookings(b.data);
      setServices(s.data);
      setStats(st.data);
    } catch {
      setMsg('Impossible de charger les réservations. Réessaie avec Actualiser.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => { load(); }, [load]);

  const onManualSaved = data => {
    setBookings(rows => rows.map(row => row.id === data.booking.id ? data.booking : row));
    setMsg(data.confirmation_status === 'sent' ? 'Paiement enregistré. Confirmation envoyée au client.' : 'Paiement enregistré. La confirmation par e-mail reste à envoyer ou à vérifier.');
    load();
  };

  const retryEmail = async (id, receiptId) => {
    setRetrying(receiptId);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/manual-payment/${receiptId}/retry-email`);
      onManualSaved(data);
    } catch { setMsg('Le paiement est conservé, mais l’e-mail n’a pas pu être renvoyé.'); }
    finally { setRetrying(''); }
  };

  const updateBooking = async (id, update) => {
    try {
      await api.patch(`/admin/bookings/${id}`, update);
      setMsg("Mise à jour effectuée");
      setTimeout(() => setMsg(""), 2000);
      load();
    } catch (e) {
      setMsg("Erreur : " + (e?.response?.data?.detail || "inconnue"));
    }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/resend-confirmation`);
      setMsg("Email renvoyé ✓");
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      setMsg("Erreur : " + (e?.response?.data?.detail || "inconnue"));
    }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem("se_admin_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "reservations.csv";
        a.click();
      });
  };

  return (
    <div className="text-text-cream" data-testid="admin-bookings-page">
      <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-4xl" data-testid="admin-bookings-title">Réservations</h1>
          <p className="text-text-cream/50 mt-2 text-sm font-light">Toutes les réservations unifiées (séances, locations).</p>
        </div>
        <button onClick={downloadCsv} className="btn-ghost" data-testid="export-csv-btn">
          <Download size={14} className="mr-2" /> Export CSV
        </button>
      </div>

      {stats && (
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <Stat label="Payées (7j à venir)" value={stats.upcoming_7d} testid="bookings-upcoming-count"/>
          <Stat label="Revenu mois (CHF)" value={stats.revenue_month_chf} testid="bookings-month-revenue"/>
          <Stat label="En attente" value={stats.total_pending} testid="bookings-pending-count"/>
          <Stat label="Total payées" value={stats.total_paid} testid="bookings-paid-count"/>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white/[0.02] border border-white/10 rounded p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-text-cream/40" />
        <select
          data-testid="filter-status"
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select
          data-testid="filter-service"
          value={filters.service_slug}
          onChange={e => setFilters({ ...filters, service_slug: e.target.value })}
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
        >
          <option value="">Tous les services</option>
          {services.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
        </select>
        <input
          type="date"
          data-testid="bookings-from-date"
          aria-label="Réservations à partir du"
          value={filters.from_date}
          onChange={e => setFilters({ ...filters, from_date: e.target.value })}
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
        />
        <span className="text-text-cream/40 text-xs">→</span>
        <input
          type="date"
          data-testid="bookings-to-date"
          aria-label="Réservations jusqu’au"
          value={filters.to_date}
          onChange={e => setFilters({ ...filters, to_date: e.target.value })}
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm"
        />
        <button onClick={load} data-testid="bookings-refresh" aria-label="Actualiser les réservations" className="ml-auto text-text-cream/60 hover:text-accent-light p-2">
          <RefreshCw size={14} />
        </button>
      </div>

      {msg && <div role="status" data-testid="bookings-message" className="mb-4 text-accent-light text-sm">{msg}</div>}

      {/* Table */}
      <div className="overflow-x-auto bg-white/[0.02] border border-white/10 rounded">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead>
            <tr className="text-left text-text-cream/40 border-b border-white/10">
              <th className="p-3 font-light">Date</th>
              <th className="p-3 font-light">Heure</th>
              <th className="p-3 font-light">Service</th>
              <th className="p-3 font-light">Client</th>
              <th className="p-3 font-light">Montant</th>
              <th className="p-3 font-light">Statut</th>
              <th className="p-3 font-light">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-text-cream/40">Chargement...</td></tr>
            )}
            {!loading && bookings.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-text-cream/40">Aucune réservation.</td></tr>
            )}
            {!loading && bookings.map(b => {
              const st = STATUS_LABELS[b.status] || STATUS_LABELS.pending;
              return (
                <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
                  <td className="p-3" data-testid={`booking-date-${b.id}`}>{fmtDate(b.date)}</td>
                  <td className="p-3 text-text-cream/70" data-testid={`booking-time-${b.id}`}>{b.start_time}–{b.end_time}</td>
                  <td className="p-3">
                    <div className="text-text-cream" data-testid={`booking-service-${b.id}`}>{b.service_name}</div>
                    <div className="text-text-cream/40 text-xs">{b.category}</div>
                  </td>
                  <td className="p-3">
                    <div data-testid={`booking-client-${b.id}`}>{b.client_prenom} {b.client_nom}</div>
                    <div className="text-text-cream/50 text-xs">{b.client_email}</div>
                    <div className="text-text-cream/40 text-xs">{b.client_tel}</div>
                  </td>
                  <td className="p-3">
                    <div className="text-accent-light" data-testid={`booking-paid-amount-${b.id}`}>CHF {b.deposit_chf}</div>
                    <div className="text-text-cream/40 text-xs" data-testid={`booking-balance-${b.id}`}>
                      sur {b.price_total_chf}
                      {b.remaining_chf > 0 && <> · reste {b.remaining_chf}</>}
                    </div>
                    <ManualPaymentHistory booking={b} retryEmail={retryEmail} retrying={retrying}/>
                  </td>
                  <td className="p-3">
                    <span data-testid={`booking-status-${b.id}`} className={`inline-block px-2 py-0.5 text-[0.65rem] uppercase tracking-wider border rounded ${st.cls}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      {b.manual_payment_context?.eligible && <button type="button" data-testid={`mark-paid-${b.id}`} onClick={() => setManualBooking(b)} className="text-xs text-accent-light hover:text-text-cream flex items-center gap-1 min-h-8"><Banknote size={14}/>Marquer comme payée</button>}
                      <button
                        data-testid={`resend-${b.id}`}
                        onClick={() => resend(b.id)}
                        className="text-xs text-text-cream/60 hover:text-accent-light flex items-center gap-1"
                        title="Renvoyer email"
                      >
                        <Mail size={12} /> Renvoyer
                      </button>
                      {b.status !== "cancelled" && (
                        <button
                          data-testid={`cancel-booking-${b.id}`}
                          onClick={() => {
                            if (confirm("Annuler cette réservation ?"))
                              updateBooking(b.id, { status: "cancelled", cancel_reason: "admin" });
                          }}
                          className="text-xs text-red-300/70 hover:text-red-300 flex items-center gap-1"
                        >
                          <X size={12} /> Annuler
                        </button>
                      )}
                      {b.status === "paid" && (
                        <button
                          data-testid={`complete-booking-${b.id}`}
                          onClick={() => updateBooking(b.id, { status: "completed" })}
                          className="text-xs text-text-cream/60 hover:text-accent-light flex items-center gap-1"
                        >
                          <Check size={12} /> Terminée
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ManualPaymentDialog booking={manualBooking} close={() => setManualBooking(null)} onSaved={onManualSaved}/>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded p-4">
      <div className="eyebrow text-text-cream/40 mb-2">{label}</div>
      <div className="font-serif text-3xl text-accent-light" data-testid={testid}>{value}</div>
    </div>
  );
}
